import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { JackstoneLogo } from "@/components/Logo";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * OAuth redirect target (registered in Google as {origin}/auth/google).
 * Reads ?code & ?state, posts them to the backend to establish the tier-1
 * company session, then forwards to the internal login page.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function GoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCompany } = useAuth();
  const [status, setStatus] = useState("working"); // working | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    queueMicrotask(async () => {
      const code = params.get("code");
      const state = params.get("state");
      const oauthError = params.get("error");

      if (oauthError) {
        if (active) {
          setStatus("error");
          setMessage("Google sign-in was cancelled or denied.");
        }
        return;
      }
      if (!code || !state) {
        if (active) {
          setStatus("error");
          setMessage("Missing authorization details. Please try again.");
        }
        return;
      }
      try {
        await api.post("/auth/google/callback", { code, state });
        await refreshCompany();
        if (active) navigate("/login", { replace: true });
      } catch (e) {
        if (active) {
          setStatus("error");
          setMessage(formatApiError(e));
        }
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#33424f] text-white p-8">
      <div className="w-full max-w-sm text-center">
        <JackstoneLogo className="h-8 w-auto mx-auto" variant="light" />
        {status === "working" ? (
          <div className="mt-8" data-testid="oauth-callback-working">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#ccb795]" />
            <p className="mt-4 text-sm text-slate-300 subtitle uppercase tracking-[0.2em]">
              Verifying company account…
            </p>
          </div>
        ) : (
          <div className="mt-8" data-testid="oauth-callback-error">
            <AlertTriangle className="w-8 h-8 mx-auto text-[#ff6b8a]" />
            <p className="mt-4 font-body text-sm text-slate-200">{message}</p>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="mt-6 h-11 px-5 bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs"
            >
              Back to sign-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
